/**
 * TaskPhaseFormDialog - Formulário de criar/editar fase de tarefa
 * (Tarefas | Retenção | Jurídico) usado dentro do gerenciador de fases
 * do card de Categoria.
 *
 * RELEASE: 2026-10-02 v1.0.0 - Criação: mesmos campos e regras do formulário
 *          da página de Fases (nome, descrição, cor, ordem, fase final, padrão).
 */
import { useEffect, useState } from 'react';
import { Columns, Loader2 } from 'lucide-react';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { HexColorPicker } from '@/components/tasks/HexColorPicker';
import type { TaskPhase, UpsertTaskPhaseInput } from '@/hooks/useTaskPhases';

const DEFAULT_COLOR = '#3b82f6';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Categoria à qual a fase pertence (fixa neste contexto) */
  categoryId: string;
  /** Fase em edição; null = nova fase */
  phase: TaskPhase | null;
  /** Ordem sugerida para nova fase */
  defaultSortOrder: number;
  isSaving: boolean;
  onSubmit: (input: UpsertTaskPhaseInput) => Promise<unknown>;
}

export function TaskPhaseFormDialog({
  open,
  onOpenChange,
  categoryId,
  phase,
  defaultSortOrder,
  isSaving,
  onSubmit,
}: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [sortOrder, setSortOrder] = useState(1);
  const [isFinal, setIsFinal] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  // Reinicializa o formulário sempre que abrir
  useEffect(() => {
    if (!open) return;
    setName(phase?.name ?? '');
    setDescription(phase?.description ?? '');
    setColor(phase?.color || DEFAULT_COLOR);
    setSortOrder(phase?.sort_order ?? defaultSortOrder);
    setIsFinal(phase?.is_final ?? false);
    setIsDefault(!!phase?.is_default);
  }, [open, phase, defaultSortOrder]);

  const handleSave = async () => {
    // Validação básica no cliente; a RPC valida novamente no servidor
    const trimmed = name.trim().slice(0, 100);
    if (!trimmed || !categoryId) return;
    const input: UpsertTaskPhaseInput = {
      p_category_id: categoryId,
      p_name: trimmed,
      p_description: description.trim().slice(0, 500) || undefined,
      p_color: color,
      p_sort_order: sortOrder,
      p_is_final: isFinal,
      p_is_default: isDefault,
    };
    if (phase) input.p_id = phase.id;
    await onSubmit(input);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        <PremiumDialogHeader
          icon={Columns}
          title={phase ? 'Editar Fase' : 'Nova Fase'}
          description={phase ? 'Atualize os dados da fase' : 'Preencha os dados da nova fase do Kanban'}
        />
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cat-phase-name">Nome *</Label>
            <Input
              id="cat-phase-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: A Fazer, Em Andamento, Concluído..."
              maxLength={100}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cat-phase-desc">Descrição</Label>
            <Textarea
              id="cat-phase-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
            />
          </div>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <HexColorPicker value={color} onChange={setColor} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-phase-order">Ordem</Label>
              <Input
                id="cat-phase-order"
                type="number"
                min={1}
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="cat-phase-final" checked={isFinal} onCheckedChange={setIsFinal} />
            <Label htmlFor="cat-phase-final" className="text-sm">Fase final</Label>
          </div>
          <div className="flex items-start gap-2">
            <Switch id="cat-phase-default" checked={isDefault} onCheckedChange={setIsDefault} />
            <div className="space-y-0.5">
              <Label htmlFor="cat-phase-default" className="text-sm">Fase padrão</Label>
              <p className="text-xs text-muted-foreground">
                Opcional. Quando marcada, novas solicitações já nascem nesta fase.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!name.trim() || isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {phase ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
